import PrivacyCheckbox from '@components/PrivacyCheckbox/PrivacyCheckbox';
import Spinner from '@components/Spinner/Spinner';
import ValidatedField from '@components/ValidatedField/ValidatedField';
import logger from '@utils/logger';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CONTACT_FORM_ENDPOINT } from 'src/constants';
import send from 'src/static/vectors/contact-send.svg';
import { ContactFormData, FormStatus, IndustryTags } from 'src/types';
import './ContactDialog.css';

interface ContactDialogProps {
	open: boolean;
	handleOpen: (o: boolean) => void;
}

const ContactDialog = ({ open, handleOpen }: ContactDialogProps) => {
	const [formStatus, setFormStatus] = useState<FormStatus>('idle');
	const dialogRef = useRef<HTMLDialogElement>(null);
	const intl = useIntl();

	useEffect(() => {
		const dialog = dialogRef.current;
		if (dialog) {
			if (open && !dialog.open) {
				dialog.showModal();
			} else {
				dialog.close();
			}
		}
	}, [open]);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		setFormStatus('loading');
		const contactFormData: ContactFormData = {
			name: formData.get('name') as string,
			lastname: formData.get('lastname') as string,
			email: formData.get('email') as string,
			phone: formData.get('phone') as string,
			company: formData.get('company') as string,
			sector: formData.get('sector') as IndustryTags,
			message: formData.get('message') as string,
			subject: 'Contact from website Trueflag.ai'
		};
		try {
			const response = await fetch(CONTACT_FORM_ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(contactFormData)
			});
			if (!response.ok) {
				throw new Error(`Error: ${response.status} - ${response.statusText}`);
			}
			const result = await response.json();
			logger.info({ result }, 'contact form submitted');
			setFormStatus('success');
		} catch (error) {
			logger.error({ err: error }, 'contact form submission failed');
			setFormStatus('error');
		}
	};

	const handleClose = () => {
		const dialog = dialogRef.current;
		if (!open && dialog?.open) {
			dialog.close();
		}
		handleOpen(false);
	};

	return (
		<dialog ref={dialogRef} id="contact-dialog">
			<header>
				<h1>
					<FormattedMessage id="contact-dialog.title" />
				</h1>
				<button onClick={handleClose}>
					<span className="icon icon-close" />
					<span>
						<FormattedMessage id="close" />
					</span>
				</button>
			</header>

			{formStatus === 'success' ? (
				<div className="success-feedback">
					<img className="symbol" src={send} alt="form sent" />
					<h1>
						<FormattedMessage id="send-success" />
					</h1>
				</div>
			) : (
				<form onSubmit={handleSubmit}>
					<fieldset id="top-fieldset">
						<ValidatedField
							name="name"
							type="text"
							placeholder={intl.formatMessage({ id: 'placeholder.name' })}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<ValidatedField
							name="lastname"
							type="text"
							placeholder={intl.formatMessage({ id: 'placeholder.lastname' })}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<ValidatedField
							name="email"
							type="email"
							placeholder={intl.formatMessage({ id: 'placeholder.email' })}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<ValidatedField
							name="phone"
							type="tel"
							placeholder={intl.formatMessage({ id: 'placeholder.tel' })}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<ValidatedField
							name="company"
							type="text"
							placeholder={intl.formatMessage({ id: 'placeholder.company' })}
							minLength={3}
							title={intl.formatMessage({ id: 'characters.title-3' })}
						/>
						<select required name="sector" defaultValue="">
							<option disabled value="">
								<FormattedMessage id="sector" />
							</option>
							{Object.values(IndustryTags).map((o: string, i: number) => (
								<option key={o + i} value={o}>
									{intl.formatMessage({ id: o })}
								</option>
							))}
						</select>
					</fieldset>
					<fieldset id="bottom-fieldset">
						<textarea rows={6} minLength={10} name="message" placeholder={intl.formatMessage({ id: 'placeholder.message' })} />
						<PrivacyCheckbox />
					</fieldset>
					<div className="feedback">
						{formStatus === 'error' && (
							<strong className="error-text error">
								<FormattedMessage id="send-error" />
							</strong>
						)}
					</div>
					<button type="submit" disabled={formStatus !== 'idle'}>
						{formStatus !== 'loading' ? <FormattedMessage id="send" /> : <Spinner />}
					</button>
				</form>
			)}
		</dialog>
	);
};

export default ContactDialog;
